import type { Typed } from 'services/api/fields/core';

type StatusType = 'active' | 'pending';

interface LocationEpisode {
  id: string;
  episodeId: string;
  owner: {
    id: string;
    name: string;
  };
  group: {
    id: string;
    name: string;
  };
}

export interface PatientSnapshot {
  name: string;
  dob: string;
  sex: string;
  groupName: string;
}

export interface MatchData {
  patient: PatientSnapshot | null;
  matchMethod: string;
  confidence: number;
  sourceLocationEpisodeId: string;
  candidateLocationEpisodeId: string;
  matchedAt: string;
}

export interface LocationEpisodeLinkOptions {
  id: string;
  status: StatusType;
  archived: boolean;
  matchData: MatchData | null;
  createdAt: string;
  updatedAt: string;
  locationEpisode?: LocationEpisode;
  [key: string]: any;
}

function getDefaults(): LocationEpisodeLinkOptions {
  return {
    id: '',
    archived: false,
    createdAt: '',
    updatedAt: '',
    status: 'pending',
    matchData: null,
    locationEpisode: {
      id: '',
      episodeId: '',
      owner: {
        id: '',
        name: '',
      },
      group: {
        id: '',
        name: '',
      },
    },
  };
}

let LocationEpisodeLink: new <T extends LocationEpisodeLinkOptions>(options: T) => Typed<_LocationEpisodeLink<T>, T>;

class _LocationEpisodeLink<T extends LocationEpisodeLinkOptions = LocationEpisodeLinkOptions> {
  id!: string;
  archived!: boolean;
  createdAt!: string;
  updatedAt!: string;
  status!: StatusType;
  matchData!: MatchData | null;
  locationEpisode!: LocationEpisode;

  constructor(options: T) {
    const opts = { ...getDefaults(), ...options };
    Object.assign(this, opts);

    this.id = opts.id;
    this.archived = opts.archived;
    this.createdAt = opts.createdAt;
    this.updatedAt = opts.updatedAt;
    this.status = opts.status;
    this.matchData = opts.matchData;
    this.locationEpisode = opts.locationEpisode!;
  }

  get isActive() {
    return this.status === 'active';
  }

  get isPending() {
    return this.status === 'pending';
  }

  get patientName() {
    return this.matchData?.patient?.name ?? '';
  }

  get dateOfBirth() {
    return this.matchData?.patient?.dob ?? '';
  }

  get sex() {
    return this.matchData?.patient?.sex ?? '';
  }

  get groupName() {
    return this.matchData?.patient?.groupName ?? '';
  }
}

// eslint-disable-next-line prefer-const
LocationEpisodeLink = _LocationEpisodeLink as typeof LocationEpisodeLink;

export default LocationEpisodeLink;
export { LocationEpisodeLink };
export type LocationEpisodeLink<T extends LocationEpisodeLinkOptions = LocationEpisodeLinkOptions> =
  _LocationEpisodeLink<T> & Omit<T, keyof _LocationEpisodeLink<T>>;
